import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { ExpandedState } from "@tanstack/react-table";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import { CollapseAllButton } from "@design-system/ViewTable/CollapseAllButton";
import { ViewTable, ViewTableHandle } from "@design-system/ViewTable/ViewTable";
import { DesignTokenTab } from "./designTokensCatalog";
import {
    buildDesignTokensConfig,
    buildDesignTokensView,
} from "./designTokensViewConfig";
import {
    buildDesignTokenRows,
    buildExpandedTokenGroups,
} from "./designTokensViewRows";

export const DesignTokensPlayground = ({ tab }: { tab: DesignTokenTab }) => {
    const location = useLocation();
    const [search, setSearch] = useState("");

    const rows = useMemo(
        () => buildDesignTokenRows({ tab, search }),
        [tab, search],
    );
    const view = useMemo(() => buildDesignTokensView(tab), [tab]);
    const config = useMemo(() => buildDesignTokensConfig(tab), [tab]);

    const tableRef = useRef<ViewTableHandle>(null);
    const [expanded, setExpanded] = useState<ExpandedState>(() =>
        buildExpandedTokenGroups(rows),
    );
    const expandedRowsRef = useRef(rows);

    // A new tab or search term produces a different row set; reopen every group
    // so results are visible, while leaving a manual collapse-all untouched.
    useEffect(() => {
        if (expandedRowsRef.current === rows) {
            return;
        }

        expandedRowsRef.current = rows;
        setExpanded(buildExpandedTokenGroups(rows));
    }, [rows]);

    // Cross-links land on /design-system/<tabId>#<copy-button-id>; scroll and
    // focus the target row once this category has rendered. Table rows mount
    // over a few frames (the virtualizer sizes itself first), so retry until
    // the target exists rather than giving up on the first miss.
    useEffect(() => {
        const targetId = location.hash.slice(1);

        if (targetId.length === 0) {
            return;
        }

        let frameId = 0;
        let attemptsLeft = 30;

        const focusTarget = () => {
            const target = document.getElementById(targetId);

            if (target !== null) {
                target.scrollIntoView({ block: "center" });
                target.focus({ preventScroll: true });
                return;
            }

            if (attemptsLeft > 0) {
                attemptsLeft -= 1;
                frameId = requestAnimationFrame(focusTarget);
            }
        };

        focusTarget();

        return () => cancelAnimationFrame(frameId);
    }, [location.hash]);

    return (
        <>
            <TablePageHeader
                isFullPage
                borderTop
                search={search}
                searchPlaceholder="Search tokens"
                onSearchChange={setSearch}
            >
                <CollapseAllButton expanded={expanded} tableRef={tableRef} />
            </TablePageHeader>
            <ViewTable
                ref={tableRef}
                data={rows}
                view={view}
                config={config}
                expanded={expanded}
                onExpandedChange={setExpanded}
                rowBorders={false}
                fixedTopOffsetPx
                nestingIndentPx={16}
            />
        </>
    );
};
